// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import type { LobbySettings, GamePreset, GameConfigSchema } from '../types/game';

interface SettingsState {
  // Current settings
  currentSettings: LobbySettings | null;

  // Available presets for current game
  availablePresets: GamePreset[];

  // Config schema (constraints)
  configSchema: GameConfigSchema | null;

  // UI state
  isAdvancedOpen: boolean;
  pendingChanges: Partial<LobbySettings>;

  // User preferences (persisted)
  hapticsEnabled: boolean;

  // Actions
  setSettings: (settings: LobbySettings) => void;
  updateSetting: (key: string, value: string | number | boolean) => void;
  applyPreset: (presetName: string) => void;
  resetToDefaults: () => void;
  openAdvanced: () => void;
  closeAdvanced: () => void;
  toggleHaptics: () => void;

  // Fetch from API
  fetchPresets: (gameId: string) => Promise<void>;
  fetchSchema: (gameId: string) => Promise<void>;

  // Clear state
  clearSettings: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSettings: null,
      availablePresets: [],
      configSchema: null,
      isAdvancedOpen: false,
      pendingChanges: {},
      hapticsEnabled: true,

      // Set current settings
      setSettings: settings => {
        logger.debug('settings_set', { settings });
        set({ currentSettings: settings, pendingChanges: {} });
      },

      // Update a single setting (accumulates in pendingChanges)
      updateSetting: (key, value) => {
        const { pendingChanges } = get();
        logger.debug('setting_updated', { key, value });
        set({ pendingChanges: { ...pendingChanges, [key]: value } });
      },

      // Apply a preset (updates currentSettings and clears pending)
      applyPreset: presetName => {
        const { availablePresets } = get();
        const preset = availablePresets.find(p => p.name === presetName);
        if (preset) {
          logger.info('preset_applied', {
            presetName,
            settings: preset.settings,
          });
          set({ currentSettings: preset.settings, pendingChanges: {} });
        } else {
          logger.warn('preset_not_found', { presetName });
        }
      },

      // Reset to default settings from schema
      resetToDefaults: () => {
        const { configSchema } = get();
        if (!configSchema) {
          logger.warn('cannot_reset_no_schema');
          return;
        }

        const defaults: LobbySettings = {
          max_players: 8,
          min_players: 3,
          score_to_win: 3,
        };

        Object.entries(configSchema).forEach(([key, config]) => {
          defaults[key] = config.default;
        });

        logger.info('settings_reset_to_defaults', { defaults });
        set({ currentSettings: defaults, pendingChanges: {} });
      },

      // UI state management
      openAdvanced: () => {
        logger.debug('advanced_settings_opened');
        set({ isAdvancedOpen: true });
      },

      closeAdvanced: () => {
        logger.debug('advanced_settings_closed');
        set({ isAdvancedOpen: false, pendingChanges: {} }); // Clear pending on close
      },

      // Toggle haptics
      toggleHaptics: () => {
        set(state => ({ hapticsEnabled: !state.hapticsEnabled }));
      },

      // Fetch presets from API
      fetchPresets: async gameId => {
        try {
          logger.info('fetching_presets', { gameId });
          const presets = await api.fetchGamePresets(gameId);
          logger.info('presets_fetched', { gameId, count: presets.length });
          set({ availablePresets: presets });

          // Auto-apply "normal" preset if no settings are currently set
          const { currentSettings } = get();
          if (!currentSettings && presets.length > 0) {
            const normalPreset = presets.find(p => p.name === 'normal');
            if (normalPreset) {
              logger.info('auto_applying_normal_preset', { gameId });
              set({
                currentSettings: normalPreset.settings,
                pendingChanges: {},
              });
            }
          }
        } catch (error) {
          logger.error('fetch_presets_failed', { gameId, error });
          set({ availablePresets: [] });
        }
      },

      // Fetch schema from API
      fetchSchema: async gameId => {
        try {
          logger.info('fetching_schema', { gameId });
          const schema = await api.fetchGameConfigSchema(gameId);
          logger.info('schema_fetched', { gameId, schema });
          set({ configSchema: schema });
        } catch (error) {
          logger.error('fetch_schema_failed', { gameId, error });
          set({ configSchema: null });
        }
      },

      // Clear all settings state
      clearSettings: () => {
        logger.debug('settings_cleared');
        set({
          currentSettings: null,
          availablePresets: [],
          configSchema: null,
          isAdvancedOpen: false,
          pendingChanges: {},
        });
      },
    }),
    {
      name: 'undef-settings',
      // Only persist user preferences, not session-specific data
      partialize: state => ({
        hapticsEnabled: state.hapticsEnabled,
      }),
    }
  )
);

// Expose store on window for E2E testing (development only)
if (typeof window !== 'undefined') {
  (window as unknown as { useSettingsStore: typeof useSettingsStore }).useSettingsStore =
    useSettingsStore;
}
